import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class PasswordErrorService {
  constructor(private translateService: TranslateService) {}

  private regexValidator(pattern: RegExp, errorKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '') as string;
      return pattern.test(value) ? null : { [errorKey]: true };
    };
  }

  createFormControl() {
    return new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      // Same logic as your original four methods, just de-duplicated
      this.regexValidator(/[A-Z]/, 'mustContainUpperCase'),
      this.regexValidator(/[a-z]/, 'mustContainLowerCase'),
      this.regexValidator(/[!@#$%^&*(),.?":{}|<>_]/, 'mustContainSpecialCharacter'),
      this.regexValidator(/\d/, 'mustContainNumber'),
    ]);
  }

  public createPasswordMatchValidator(passwordName: string, passwordRepeatName: string) {
    return (control: AbstractControl) => {
      const passwordControl = control.get(passwordName);
      const passwordRepeatControl = control.get(passwordRepeatName);

      if (passwordControl?.value !== passwordRepeatControl?.value) {
        if (passwordRepeatControl?.errors) {
          passwordRepeatControl.errors['notMatching'] = true;
        } else {
          passwordRepeatControl?.setErrors({ notMatching: true });
        }
      } else {
        if (passwordRepeatControl?.errors) {
          delete passwordRepeatControl.errors['notMatching'];
        } else {
          passwordRepeatControl?.setErrors(null);
        }
      }
      return null;
    };
  }

  public getPasswordErr(control: AbstractControl) {
    // Required → same as before
    if (control.hasError('required')) {
      return this.translateService.instant('ADMINISTRATION.VALIDATIONS.REQUIREMENTS');
    }

    const parts: string[] = [];

    // minlength wording preserved
    const hasMinLengthError = control.hasError('minlength');
    if (hasMinLengthError) {
      parts.push(
        `${this.translateService.instant('ADMINISTRATION.VALIDATIONS.ATLEAST_TEN_CHARS')} ${this.translateService.instant('ADMINISTRATION.VALIDATIONS.CHARACTERS')}`,
      );
    }

    // Collect missing criteria in one place (replaces getUpper/Lower/Special/NumberPart duplication)
    const missing: string[] = [];
    if (control.hasError('mustContainUpperCase')) {
      missing.push(this.translateService.instant('ADMINISTRATION.VALIDATIONS.UPPER'));
    }
    if (control.hasError('mustContainLowerCase')) {
      missing.push(this.translateService.instant('ADMINISTRATION.VALIDATIONS.LOWER'));
    }
    if (control.hasError('mustContainSpecialCharacter')) {
      missing.push(this.translateService.instant('ADMINISTRATION.VALIDATIONS.SPECIAL'));
    }
    if (control.hasError('mustContainNumber')) {
      missing.push(this.translateService.instant('ADMINISTRATION.VALIDATIONS.NUMBER'));
    }

    if (missing.length > 0) {
      const starter = hasMinLengthError
        ? this.translateService.instant('ADMINISTRATION.VALIDATIONS.WITH')
        : this.translateService.instant('ADMINISTRATION.VALIDATIONS.CONTAINING');

      // Join exactly as before: comma-separated then “CHARACTER”
      parts.push(`${starter} ${missing.join(', ')} ${this.translateService.instant('ADMINISTRATION.VALIDATIONS.CHARACTER')}`);
    }

    return parts.join(' ').trim();
  }

  getConfirmPwdErr(control: AbstractControl) {
    if (control.hasError('required')) {
      return this.translateService.instant('ADMINISTRATION.VALIDATIONS.REQUIRED');
    } else if (control.hasError('notMatching')) {
      return this.translateService.instant('ADMINISTRATION.VALIDATIONS.DO_NOT_MATCH');
    } else {
      return '';
    }
  }
}
